/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AssetsService, FormHelpersService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import { environment } from 'environments/environment';
import { filter, tap } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-highlight-reel-play-pro-modal',
  templateUrl: './highlight-reel-play-pro-modal.component.html',
  styleUrls: ['./highlight-reel-play-pro-modal.component.scss'],
})
export class HighlightReelPlayProModalComponent implements OnInit {
  @Input() userEvents: any = [];
  @Input() playInfo: any = {};
  @Input() purchaseId: number;
  @Input() readonly: boolean;
  @Input() maxPlays: number;
  @Input() searchEvents = false;
  @Input() isProFeedback = false;
  @Input() eventOptions?: any;

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('videoUpload') videoUpload: ElementRef;

  selectedTeams: any[];
  playInfoForm: FormGroup;
  viewType: string;
  uploadingVideo = false;
  uploadProgress = 0;
  fileLoaded = false;
  fileDrag = false;
  file: File;

  readonly MODAL_TYPES = Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES;

  addTypeDisplay = 'Game';

  constructor(public formHelpersService: FormHelpersService, private assetsService: AssetsService) {}

  ngOnInit() {
    const gameInfo = this.playInfo.gameInfo || {};
    this.viewType = this.playInfo.type || this.MODAL_TYPES.SELECT;

    this.playInfoForm = new FormGroup({
      id: new FormControl(this.playInfo.id),
      index: new FormControl(this.playInfo.index),
      eventId: new FormControl(this.playInfo.eventId, [Validators.required]),
      eventName: new FormControl(gameInfo.eventName, [Validators.required]),
      gameId: new FormControl(this.playInfo.gameId, [Validators.required]),
      gameName: new FormControl(gameInfo.name, [Validators.required]),
      thumbnail: new FormControl(gameInfo.thumbnail, []),
      gameDate: new FormControl(gameInfo.date, []),
      jerseyNumber: new FormControl(this.playInfo.jerseyNumber, [Validators.required]),
      videoUrl: new FormControl(this.playInfo.videoUrl),
      type: new FormControl(gameInfo.type),
    });

    this.selectType(null, this.viewType);
  }

  close() {
    this.closeModal.emit({});
  }

  save() {
    if (this.playInfoForm.invalid) {
      this.formHelpersService.markFieldsAsDirty(this.playInfoForm);
      return;
    }
    const formValues = this.playInfoForm.value;

    const gameInfo = {
      eventName: formValues.eventName,
      name: formValues.gameName,
      thumbnail: formValues.thumbnail,
      date: formValues.date,
    };

    this.closeModal.emit({ play: formValues, gameInfo, index: this.playInfo.index });
  }

  selectType($event, newType: string) {
    if ($event) {
      $event.preventDefault();
    }

    this.viewType = newType;

    switch (newType) {
      case this.MODAL_TYPES.ADD:
        this.addTypeDisplay = 'Video';
        this.playInfoForm.get('type').setValue(Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.ADD);
        this.playInfoForm.get('eventId').clearValidators();
        this.playInfoForm.get('gameId').clearValidators();
        this.playInfoForm.get('videoUrl').setValidators(Validators.required);
        break;

      case this.MODAL_TYPES.SELECT:
        this.addTypeDisplay = 'Game';
        this.playInfoForm.get('type').setValue(Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.SELECT);
        this.playInfoForm.get('eventId').setValidators(Validators.required);
        this.playInfoForm.get('gameId').setValidators(Validators.required);
        this.playInfoForm.get('videoUrl').clearValidators();
        break;
    }
  }

  get thumbnail() {
    return this.playInfoForm.get('thumbnail').value;
  }

  get videoUrl() {
    return this.playInfoForm.get('videoUrl').value;
  }

  onDrop($event) {
    $event.preventDefault();
    this.fileDrag = false;
    this.fileChanged({ target: $event.dataTransfer });
  }

  onDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = true;
  }

  onDragLeave(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = false;
  }

  fileChanged(event) {
    if (!event.target.files) {
      return;
    }

    this.uploadingVideo = true;
    this.uploadProgress = 0;

    const file: File = event.target.files[0];
    const extension = file.name.slice(file.name.lastIndexOf('.') + 1);

    const newFilename = new Date().getTime() + `.${extension}`;

    const uploadLocation = this.isProFeedback ? '_pro_feedback_uploads' : '_hr_user_uploads';
    this.assetsService
      .uploadFileToBucket(
        `video_assets/${uploadLocation}/${this.purchaseId}/${newFilename}`,
        file,
        environment.bucketName
      )
      .pipe(
        tap((response) => {
          if (response.loaded && response.total) {
            this.uploadProgress = Math.ceil((100 * response.loaded) / response.total);
          }
        }),
        filter((response) => response.ok && response.type === HttpEventType.Response)
      )
      .subscribe((uploadResponse) => {
        this.playInfoForm
          .get('videoUrl')
          .setValue(environment.fastly.main + '/' + `${uploadLocation}/${this.purchaseId}/${newFilename}`);
        this.uploadingVideo = false;
      });
  }
}
